import { useState, useCallback, useEffect } from 'react'
import { Uuid } from '@common'
import {
  GoogleDriveConnection,
  GoogleDriveFolder,
  GoogleDriveSyncHistoryItem,
  GoogleDrivePickerItem,
  GoogleDriveResolvedItem
} from '../types'
import {
  getGoogleDriveAuthUrl,
  getGoogleDriveStatus,
  getGoogleDriveFolders,
  getGoogleDriveSyncHistory,
  connectGoogleDrive,
  disconnectGoogleDrive,
  createSyncConfiguration,
  updateSyncConfiguration,
  deleteSyncConfiguration,
  triggerSync,
  resolveGoogleDriveUrl,
  linkGoogleDriveItems,
  importGoogleDriveItems
} from '@src/service/services/google-drive'
import { HttpClient } from '@src/service'

export interface UseGoogleDriveConnectionProps {
  httpClient: HttpClient
  organizationId: Uuid
}

export function useGoogleDriveConnection({ httpClient, organizationId }: UseGoogleDriveConnectionProps) {
  const [connection, setConnection] = useState<GoogleDriveConnection | null>(null)
  const [folders, setFolders] = useState<GoogleDriveFolder[]>([])
  const [syncHistory, setSyncHistory] = useState<GoogleDriveSyncHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const status = await getGoogleDriveStatus(httpClient)({ organization: organizationId })
      setConnection(status || null)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch connection status')
    } finally {
      setIsLoading(false)
    }
  }, [httpClient, organizationId])

  const initiateConnect = useCallback(async () => {
    setError(null)
    try {
      const response = await getGoogleDriveAuthUrl(httpClient)({ organization: organizationId })
      if (response?.url) {
        // Open OAuth popup
        const width = 600
        const height = 700
        const left = window.screenX + (window.outerWidth - width) / 2
        const top = window.screenY + (window.outerHeight - height) / 2
        window.open(
          response.url,
          'google-drive-auth',
          `width=${width},height=${height},left=${left},top=${top}`
        )
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initiate connection')
    }
  }, [httpClient, organizationId])

  const completeConnect = useCallback(async (code: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await connectGoogleDrive(httpClient)({ organization: organizationId }, { code })
      if (response?.success) {
        await fetchStatus()
      }
      return response
    } catch (err: any) {
      setError(err.message || 'Failed to complete connection')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [httpClient, organizationId, fetchStatus])

  const disconnect = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      await disconnectGoogleDrive(httpClient)({ organization: organizationId })
      setConnection(null)
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect')
    } finally {
      setIsLoading(false)
    }
  }, [httpClient, organizationId])

  const fetchFolders = useCallback(async (parentId?: string) => {
    setError(null)
    try {
      const response = await getGoogleDriveFolders(httpClient)({ organization: organizationId, parentId })
      setFolders(response?.folders || [])
      return response?.folders || []
    } catch (err: any) {
      setError(err.message || 'Failed to fetch folders')
      return []
    }
  }, [httpClient, organizationId])

  const addSyncConfig = useCallback(async (
    googleFolderId: string,
    googleFolderName: string,
    targetDataRoomPath: string,
    syncSubfolders?: boolean,
    fileTypeFilter?: string[]
  ) => {
    setError(null)
    try {
      const response = await createSyncConfiguration(httpClient)(
        { organization: organizationId },
        { googleFolderId, googleFolderName, targetDataRoomPath, syncSubfolders, fileTypeFilter }
      )
      if (response?.success) {
        await fetchStatus()
      }
      return response
    } catch (err: any) {
      setError(err.message || 'Failed to create sync configuration')
      throw err
    }
  }, [httpClient, organizationId, fetchStatus])

  const resolveUrl = useCallback(async (url: string) => {
    setError(null)
    try {
      const response = await resolveGoogleDriveUrl(httpClient)(
        { organization: organizationId },
        { url }
      )
      return response?.item
    } catch (err: any) {
      setError(err.message || 'Failed to resolve Google Drive link')
      throw err
    }
  }, [httpClient, organizationId])

  const linkItems = useCallback(async (
    items: Array<GoogleDrivePickerItem | GoogleDriveResolvedItem>,
    dataRoomPath: string,
    mirrorStructure: boolean,
    checklistKey?: string
  ) => {
    setError(null)
    try {
      const response = await linkGoogleDriveItems(httpClient)(
        { organization: organizationId },
        { items, dataRoomPath, mirrorStructure, checklistKey }
      )
      await fetchStatus()
      return response
    } catch (err: any) {
      setError(err.message || 'Failed to link Google Drive items')
      throw err
    }
  }, [httpClient, organizationId, fetchStatus])

  const importItems = useCallback(async (
    items: Array<GoogleDrivePickerItem | GoogleDriveResolvedItem>,
    dataRoomPath: string,
    mirrorStructure: boolean,
    checklistKey?: string
  ) => {
    setError(null)
    try {
      const response = await importGoogleDriveItems(httpClient)(
        { organization: organizationId },
        { items, dataRoomPath, mirrorStructure, checklistKey, materializeNativeDocsAsPdf: true }
      )
      await fetchStatus()
      await fetchHistory()
      return response
    } catch (err: any) {
      setError(err.message || 'Failed to import Google Drive items')
      throw err
    }
  }, [httpClient, organizationId, fetchStatus])

  const updateConfig = useCallback(async (
    configId: Uuid,
    updates: {
      targetDataRoomPath?: string
      syncEnabled?: boolean
      syncSubfolders?: boolean
      fileTypeFilter?: string[]
    }
  ) => {
    setError(null)
    try {
      const response = await updateSyncConfiguration(httpClient)(
        { organization: organizationId, id: configId },
        updates
      )
      if (response?.success) {
        await fetchStatus()
      }
      return response
    } catch (err: any) {
      setError(err.message || 'Failed to update sync configuration')
      throw err
    }
  }, [httpClient, organizationId, fetchStatus])

  const deleteConfig = useCallback(async (configId: Uuid) => {
    setError(null)
    try {
      const response = await deleteSyncConfiguration(httpClient)({
        organization: organizationId,
        id: configId
      })
      if (response?.success) {
        await fetchStatus()
      }
      return response
    } catch (err: any) {
      setError(err.message || 'Failed to delete sync configuration')
      throw err
    }
  }, [httpClient, organizationId, fetchStatus])

  const startSync = useCallback(async (configurationId?: Uuid) => {
    setIsSyncing(true)
    setError(null)
    try {
      const response = await triggerSync(httpClient)(
        { organization: organizationId },
        { configurationId }
      )
      await fetchStatus()
      await fetchHistory()
      return response
    } catch (err: any) {
      setError(err.message || 'Failed to trigger sync')
      throw err
    } finally {
      setIsSyncing(false)
    }
  }, [httpClient, organizationId, fetchStatus])

  const fetchHistory = useCallback(async (_configurationId?: Uuid) => {
    setError(null)
    try {
      const response = await getGoogleDriveSyncHistory(httpClient)({ organization: organizationId })
      setSyncHistory(response?.history || [])
      return response?.history || []
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sync history')
      return []
    }
  }, [httpClient, organizationId])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  return {
    connection,
    folders,
    syncHistory,
    isLoading,
    isSyncing,
    error,
    fetchStatus,
    initiateConnect,
    completeConnect,
    disconnect,
    fetchFolders,
    addSyncConfig,
    updateConfig,
    deleteConfig,
    startSync,
    fetchHistory,
    resolveUrl,
    linkItems,
    importItems
  }
}
